import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Radio } from "@/components/Radio/Radio";
import { Toggle } from "@/components/Toggle/Toggle";

import { TrackerEntityKind, TrackerImportPriorityPreset } from "@/enum/tracker.enum";

import { useCreateTrackerListImportMutation } from "@/pages/Private/redux/profileTracker/trackerSources.api";

import { classNames } from "@/utils/classNames";

interface TrackerListImportFormProps {
	/** The tracker's own list — the destination. */
	listId: number;
	/** Lists that can be imported from, already excluding this tracker. */
	sourceLists: { id: number; name: string }[];
	/** Lifts the typed term so the owner can re-query rather than filter one page. */
	onSearchLists: (value: string) => void;
	isSearchingLists?: boolean;
}

const PRESETS = [
	TrackerImportPriorityPreset.ALL,
	TrackerImportPriorityPreset.QUALIFIED,
	TrackerImportPriorityPreset.P1,
	TrackerImportPriorityPreset.P1_P2,
];

/**
 * Importing an existing lead list into the tracker (PREC-3417).
 *
 * The selection is saved with the source rather than applied once, so it can be re-evaluated —
 * which is what makes "keep this import live" possible at all.
 */
export const TrackerListImportForm: FunctionComponent<TrackerListImportFormProps> = ({
	listId,
	sourceLists,
	onSearchLists,
	isSearchingLists,
}) => {
	const { t } = useTranslation();

	const [createImport, { isLoading }] = useCreateTrackerListImportMutation();

	const [sourceListId, setSourceListId] = useState<number | null>(null);
	const [entityKind, setEntityKind] = useState(TrackerEntityKind.PERSON);
	const [preset, setPreset] = useState(TrackerImportPriorityPreset.ALL);
	const [isLive, setIsLive] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const options: AutoCompleteItem[] = sourceLists.map((list) => ({
		title: list.name,
		id: list.id,
	}));

	const selected = options.find((option) => option.id === sourceListId);

	const handleSubmit = async () => {
		if (!sourceListId) {
			return;
		}

		setError(null);

		try {
			await createImport({
				listId,
				body: { listId: sourceListId, entityKind, priorityPreset: preset, isLive },
			}).unwrap();

			setSourceListId(null);
			setIsLive(false);
		} catch {
			setError(t("profileTracker.sources.errors.importFailed"));
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div>
				<div className="mb-1.5 text-ssm font-semibold text-text-meta">
					{t("profileTracker.sources.import.listLabel")}
				</div>
				{/*
				 * Type-ahead rather than a plain select: an account can have hundreds of lists,
				 * and scrolling a flat menu to find one by name is the slow half of this form.
				 */}
				<AutoComplete
					floating
					// The owner re-queries on the typed term, so filtering the page again here
					// would hide results the server just returned.
					disableLocalFilter
					data={options}
					handleReset={() => {
						setSourceListId(null);
						onSearchLists("");
					}}
					handleSearch={onSearchLists}
					handleSelect={(value?: AutoCompleteItem) => setSourceListId(Number(value?.id) || null)}
					isLoading={isSearchingLists}
					placeholder={t("profileTracker.sources.import.listPlaceholder")}
					selectedItem={selected?.title?.toString() ?? ""}
					testId="tracker-import-list"
				/>
			</div>

			<div>
				<div className="mb-1.5 text-ssm font-semibold text-text-meta">
					{t("profileTracker.sources.import.trackAsLabel")}
				</div>
				<div className="flex flex-col gap-2">
					{[
						{ kind: TrackerEntityKind.PERSON, labelKey: "trackPeople" },
						{ kind: TrackerEntityKind.ORGANIZATION, labelKey: "trackCompanies" },
					].map((option) => (
						<div
							key={option.kind}
							className={classNames(
								"flex cursor-pointer items-center gap-2.5 rounded-[9px] border px-3 py-2.5 duration-100",
								entityKind === option.kind
									? "border-brand-light bg-blue-action-200"
									: "border-gray-200 hover:border-brand-light"
							)}
							onClick={() => setEntityKind(option.kind)}
						>
							<Radio isChecked={entityKind === option.kind} name="trackerImportEntityKind" />
							<span className="text-ssm font-semibold">
								{t(`profileTracker.sources.import.${option.labelKey}`)}
							</span>
						</div>
					))}
				</div>
			</div>

			<div>
				<div className="mb-1.5 text-ssm font-semibold text-text-meta">
					{t("profileTracker.sources.import.whichLabel")}
				</div>
				<div className="flex flex-col gap-2">
					{PRESETS.map((option) => (
						<div
							key={option}
							className={classNames(
								"flex cursor-pointer items-center gap-2.5 rounded-[9px] border px-3 py-2.5 duration-100",
								preset === option
									? "border-brand-light bg-blue-action-200"
									: "border-gray-200 hover:border-brand-light"
							)}
							data-testid={`tracker-import-preset-${option}`}
							onClick={() => setPreset(option)}
						>
							<Radio isChecked={preset === option} name="trackerImportPreset" />
							<div className="flex-1">
								<div className="text-ssm font-semibold">
									{t(`profileTracker.sources.import.presets.${option}`)}
								</div>
								<div className="text-xs text-text-meta">
									{t(`profileTracker.sources.import.presets.${option}_DESCRIPTION`)}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="flex items-center gap-3 rounded-[10px] border border-border-light bg-blue-action-200 p-3.5">
				<div className="flex-1">
					<div className="text-ssm font-semibold">
						{t("profileTracker.sources.import.liveTitle")}
					</div>
					<div className="mt-0.5 text-xs text-text-meta">
						{t("profileTracker.sources.import.liveDescription")}
					</div>
				</div>
				<Toggle
					isChecked={isLive}
					name="trackerImportIsLive"
					testId="tracker-import-live"
					onChange={() => setIsLive((current) => !current)}
				/>
			</div>

			{error && <div className="text-ssm text-status-error">{error}</div>}

			<div className="flex justify-end">
				<button
					className="cursor-pointer rounded-lg bg-brand-light px-4 py-2.5 text-ssm font-semibold text-white duration-200 hover:bg-blue-action-dark disabled:cursor-not-allowed disabled:bg-gray-200"
					disabled={!sourceListId || isLoading}
					type="button"
					onClick={handleSubmit}
				>
					{t("profileTracker.sources.import.submit")}
				</button>
			</div>
		</div>
	);
};
